import { apiErrorMessage } from '@agos/api-client'
import {
  getGetAttendanceMonthlyQueryKey,
  getGetAttendanceQueryKey,
  getGetAttendanceSelfTodayQueryKey,
  usePatchAttendanceSelfToday,
} from '@agos/api-client/hr'
import { T, tt, useModalFocus } from '@agos/shared'
import { BUTTON_PRIMARY, BUTTON_SECONDARY, cn, Spinner, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Clock } from 'lucide-react'
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  canSaveShift,
  selfAmendPayload,
  selfShiftDay,
  selfShiftProblems,
  shiftFormOf,
  shiftProblemText,
  type AttendanceRecord,
  type ShiftForm,
} from './time-sheet'

/**
 * *Upraviť časy zmeny* - somebody corrects their own arrival and departure for today, FRONTEND.md §9.760, owner O123,
 * a port of the dialog behind legacy's sidebar pencil (`sidebar-attendance-widget.tsx`, `EditModal`).
 *
 * **What it checks is the manager's rule, not a second one.** The problems are `shiftProblems` with no overnight,
 * named in the manager's own sentences (`shiftProblemText`), and *Uložiť* stays disabled while any of them blocks; a
 * shift over sixteen hours is questioned and not refused, as it is there. There is no *na druhý deň* box: a departure
 * after midnight is in the future while today's record is the current one (BACKEND.md §10A.414).
 *
 * **Yesterday's shift, carried over past midnight (§9.761, BACKEND.md §10A.415)**, is the one kind that can end on the
 * next day, so for it the times choose: a departure earlier than the arrival is after midnight, the hint under
 * *Odchod* says so, and a time in the future is judged across midnight (`selfShiftProblems`). The day under the
 * heading reads *Včera*.
 *
 * **The server has the last word and says it here.** `PATCH /attendance/self/today` checks the times against the
 * stored row and its own clock and answers a refusal as a 400 carrying its sentence; that sentence is shown inside the
 * dialog, which stays open with what was typed, because the person's next move is to correct the time it names.
 *
 * **It is a modal like the others**: the dialog role and the modal attribute, named by its heading, the focus hook
 * with *Zrušiť* marked as the cancel, and Escape and the scrim close it only while nothing is being saved - a close
 * in the middle of a save would leave the refusal with nowhere to be read. The scrim is the wrapper, so the role is
 * on it and the trap is bounded by the card, `ConfirmDialog`'s pattern. Described here in words rather than by the
 * attribute names, because the overlay ratchets read this file's source and a name in a comment would answer for a
 * name dropped from the markup.
 *
 * **Rendered on `document.body`**: on a phone the sidebar is a drawer, and a fixed overlay inside a transformed parent
 * is placed against that parent rather than the screen.
 */
export function SelfAttendanceDialog({
  workDate,
  carriedOver,
  record,
  onClose,
}: {
  /** The shift's work date as the server answered it, which is the date the arrival is on. */
  workDate: string
  /** Whether that shift is yesterday's carried over past midnight - `isCarriedOver`, §9.761. */
  carriedOver: boolean
  /** The shift's record as the sidebar read it, or null before the first *Príchod*. */
  record: AttendanceRecord | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<ShiftForm>(() => shiftFormOf(record))
  const [refusal, setRefusal] = useState<string | null>(null)

  const amend = usePatchAttendanceSelfToday({
    mutation: {
      onSuccess: () => {
        // The sidebar's own read, and *Dochádzka*'s two, whose rows now carry the correction (§10A.414).
        void queryClient.invalidateQueries({ queryKey: getGetAttendanceSelfTodayQueryKey() })
        void queryClient.invalidateQueries({ queryKey: getGetAttendanceQueryKey() })
        void queryClient.invalidateQueries({ queryKey: getGetAttendanceMonthlyQueryKey() })
        toast({ title: tt('Dochádzka upravená') })
        onClose()
      },
      onError: (caught) => setRefusal(apiErrorMessage(caught, tt('Časy sa neuložili. Skúste to znova.'))),
    },
  })
  const saving = amend.isPending

  // The device's clock, read on every render, so a dialog left open does not keep checking against the minute it
  // opened in - across midnight for a carried-over shift, and on today's date only for today's.
  const problems = selfShiftProblems(form, workDate, carriedOver, new Date())

  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, saving ? null : onClose)

  // A refusal is about the times it was sent; once they change it is about nothing on the screen.
  const edit = (next: ShiftForm) => {
    setForm(next)
    setRefusal(null)
  }
  const save = () => {
    setRefusal(null)
    amend.mutate({ data: selfAmendPayload(workDate, form, carriedOver) })
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="self-attendance-title"
      data-self-attendance-dialog
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="bg-card text-foreground border border-border rounded-2xl w-full max-w-sm shadow-2xl"
      >
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
          <Clock className="w-4 h-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <h2 id="self-attendance-title" className="font-bold text-sm">
              <T>Upraviť časy zmeny</T>
            </h2>
            <p
              className="text-[11px] text-muted-foreground"
              data-self-attendance-day={carriedOver ? 'yesterday' : 'today'}
            >
              {selfShiftDay(workDate, carriedOver)}
            </p>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor="self-attendance-in" className={LABEL}>
                <T>Príchod</T>
              </label>
              <input
                id="self-attendance-in"
                type="time"
                value={form.checkIn}
                onChange={(event) => edit({ ...form, checkIn: event.target.value })}
                className={INPUT}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="self-attendance-out" className={LABEL}>
                <T>Odchod</T>
              </label>
              <input
                id="self-attendance-out"
                type="time"
                value={form.checkOut}
                onChange={(event) => edit({ ...form, checkOut: event.target.value })}
                aria-describedby={
                  carriedOver ? 'self-attendance-out-hint self-attendance-next-day-hint' : 'self-attendance-out-hint'
                }
                className={INPUT}
              />
            </div>
          </div>
          {/* What an empty departure means, because emptying it is how a shift closed by mistake is opened again. */}
          <p id="self-attendance-out-hint" className="text-[11px] text-muted-foreground" data-self-attendance-out-hint>
            <T>Prázdny odchod znamená, že zmena ešte beží.</T>
          </p>
          {/* §9.761 - only on yesterday's shift, the one kind whose departure can be after midnight, and the reason
              a departure earlier than the arrival is not refused here as it is on today's. */}
          {carriedOver && (
            <p
              id="self-attendance-next-day-hint"
              className="text-[11px] text-muted-foreground"
              data-self-attendance-next-day-hint
            >
              <T>Čas skorší ako príchod je po polnoci.</T>
            </p>
          )}

          <div className="space-y-1.5">
            <label htmlFor="self-attendance-notes" className={LABEL}>
              <T>Poznámka</T>
            </label>
            <textarea
              id="self-attendance-notes"
              rows={2}
              // The server keeps five hundred characters of it; saying so here beats cutting it off there.
              maxLength={500}
              value={form.notes}
              onChange={(event) => edit({ ...form, notes: event.target.value })}
              placeholder={tt('napr. zmena trvania, dovolenka...')}
              className={cn(INPUT, 'resize-none')}
            />
          </div>

          {problems.length > 0 && (
            <div className="space-y-1" data-shift-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  data-shift-problem={problem.kind}
                  className={cn(
                    'flex items-start gap-1.5 text-xs',
                    problem.kind === 'implausible' ? 'text-amber-500' : 'text-red-500',
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>{shiftProblemText(problem, false)}</span>
                </p>
              ))}
            </div>
          )}

          {refusal !== null && (
            <p
              role="alert"
              data-self-attendance-refusal
              className="flex items-start gap-1.5 rounded-lg border border-destructive/40 bg-destructive/5 p-2.5 text-xs text-destructive"
            >
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>{refusal}</span>
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            data-dialog-cancel
            onClick={onClose}
            disabled={saving}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            data-self-attendance-save
            onClick={save}
            disabled={saving || !canSaveShift(problems)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

const LABEL = 'text-xs text-muted-foreground uppercase tracking-wide font-medium block'

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'
